import React, {
  FocusEventHandler,
  HTMLProps,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useOnClickOutside } from 'usehooks-ts';

import { SearchParams, SearchType } from '@/components/search/types';
import { AutoCompletionList } from '@/components/search/v1/list/AutoCompletionList/AutoCompletionList';
import { SearchPanelList } from '@/components/search/v1/list/SearchPanelList';
import { validateSearchQuery } from '@/components/search/v1/utils/searchValidations';
import { useCombineRefs } from '@/hooks/useCombineRefs';

import useSearchPanels from '../hooks/useSearchPanels';
import SearchInput from '../searchInput/SearchInput';
import { SEARCH_ADD_TOGGLE_STORAGE_KEY } from '../utils/helpers';

import { FieldErrorExtendedSearch } from './FieldErrorExtendedSearch/FieldErrorExtendedSearch';
import SearchTypeButtons from './SearchPanel/SearchTypeButtons';
import ToggleAddSearchResult from './SearchPanel/ToggleAddSearchResult';

import styles from './ExtendedSearch.module.css';

interface ExtendedSearchInputProps extends HTMLProps<HTMLInputElement> {
  loading?: boolean;
  hideSearchIcon?: boolean;
  defaultValues?: SearchParams;
  isWithinAdvancedSearch?: boolean;
  handleResetFieldWithinAdvancedSearch?: () => void;

  clearSearchField?: boolean;
  /** Callback die wordt afgevuurd als de gebruiker op enter drukt na het invoeren van een searchType / searchInput string. */
  onUserSearchIntent?: (searchParams: SearchParams, add?: boolean) => void;

  /** Callback die wordt gebruikt om het geavanceerde zoek dialoog te openen. */
  handleOpenAdvancedSearchDialog?: () => void;
}

function ExtendedSearch({
  loading,
  onUserSearchIntent,
  defaultValues,
  className,
  isWithinAdvancedSearch = false,
  handleResetFieldWithinAdvancedSearch,
  clearSearchField,
  hideSearchIcon = false,
  handleOpenAdvancedSearchDialog,
  ref,
}: ExtendedSearchInputProps & { ref?: React.Ref<HTMLInputElement> }) {
  // SearchParams construction
  const [query, setQuery] = useState<string>('');
  const [searchType, setSearchType] = useState<SearchType>('empty');
  const [selectedSearchDate, setSelectedSearchDate] = useState<{
    shortDescription: string;
    description: string;
    date: string;
  }>({
    shortDescription: '',
    description: '',
    date: '',
  });

  /** SearchQuery input validation */
  const {
    isValidSingle,
    isValidMultiple,
    isValidRange,
    isValidJson,
    isValidMaterieelTypeValue,
    isValidOmloopValue,
    isValidBijzonderheidscode,
    isValidSingleOrMultipleValue,
    isValidSingleMultipleOrRangeValue,
    hasError,
    createSearchParams,
  } = useMemo(() => ({ ...validateSearchQuery({ query, searchType, isWithinAdvancedSearch }) }), [query, searchType]);

  // Lists for Dates, History and Autocompletion
  const autoCompletionListRef = useRef<HTMLDivElement>(null);
  const daySelectionListRef = useRef<HTMLDivElement>(null);
  const [closeSearchPanel, setCloseSearchPanel] = useState<boolean>(false);
  /** listItemIsSelected is used for the autocompletion */
  const [listItemIsSelected, setListItemIsSelected] = useState<boolean>(false);

  const setSelectedItemInsideInput = useCallback(
    (item: string) => {
      if (item !== undefined) {
        const workingQuery = query.trim().replaceAll(';', ',').toUpperCase();
        // Split the original string by commas to get individual values
        const values = workingQuery.split(/[,]+/);
        // Replace the last value with the new value
        values[values.length - 1] = `${item}`;

        // Join the values back into a string
        setQuery(values.join(','));
        if (inputElemRef?.current) {
          inputElemRef.current.focus();
          setFocused(true);
        }
      }
    },
    [query],
  );

  // UX of the SearchField
  const extendedSearchDivRef = useRef<HTMLDivElement>(null);
  const inputElemRef = useRef<HTMLInputElement>(null);
  const inputRef = useCombineRefs(ref ?? null, inputElemRef);
  const [focused, setFocused] = useState(false);
  const [inputChipIsActive, setInputChipIsActive] = useState<boolean>(true);
  const [showJSONChip, setShowJSONChip] = useState<boolean>(false);
  const programmaticDateUpdate = useRef(false);

  /** Determine which searchPanel to show. */
  const showPanel = useMemo<false | 'searchPanel' | 'searchDates' | 'materieeltypen'>(() => {
    if (closeSearchPanel) return false;
    if (searchType === 'empty' && (focused || isValidSingleMultipleOrRangeValue)) return 'searchPanel';
    if (searchType === 'rollingStockType' && (isValidMaterieelTypeValue || query.length === 0)) return 'materieeltypen';
    if (
      ((['train', 'trainAndRollingStock'].includes(searchType) && isValidSingleMultipleOrRangeValue) ||
        (searchType === 'omloop' && isValidOmloopValue) ||
        (searchType === 'bijzonderheidscode' && isValidBijzonderheidscode)) &&
      query.length > 0 &&
      !isWithinAdvancedSearch &&
      !hasError
    )
      return 'searchDates';
    return false;
  }, [
    searchType,
    closeSearchPanel,
    focused,
    isValidSingleOrMultipleValue,
    isValidSingleMultipleOrRangeValue,
    isValidBijzonderheidscode,
    isValidJson,
    isValidOmloopValue,
    query,
    isValidMaterieelTypeValue,
  ]);

  /** NEW LIST HOOK */
  const {
    daySelectionVariations,
    materieeltypen,
    searchHistory,
    updateSearchHistory,
    arrowDown,
    arrowUp,
    selectedIndex,
    setSelectedIndex,
    addSearchResults,
    setAddSearchResults,
  } = useSearchPanels({
    searchType,
    showPanel,
    isWithinAdvancedSearch,
  });
  const onClickAddSearchResults = (checked: boolean) => {
    setAddSearchResults(checked);
    sessionStorage?.setItem(SEARCH_ADD_TOGGLE_STORAGE_KEY, String(checked));
    inputElemRef?.current?.focus();
  };

  const handleShowJSONButtonClicks = () => {
    let clickCount = 0;
    let lastClickTime = 0;
    return () => {
      const currentTime = Date.now();
      if (currentTime - lastClickTime > 1000) clickCount = 0; // Reset after a 1 second.
      lastClickTime = currentTime;
      clickCount++;
      if (clickCount >= 20) setShowJSONChip(true);
    };
  };

  // Whenever the user updates the searchInput handle it via this callback.
  const inputChangeHandler = useCallback((value: string) => {
    setCloseSearchPanel(false);
    setQuery(value);
  }, []);

  /** Close all open searchPanels and unfocus the input field. */
  const closeExtendedSearch = () => {
    setCloseSearchPanel(true);
    setFocused(false);
    setSelectedIndex(0);
  };

  useOnClickOutside(autoCompletionListRef as React.RefObject<HTMLElement>, closeExtendedSearch);
  useOnClickOutside(daySelectionListRef as React.RefObject<HTMLElement>, closeExtendedSearch);
  useOnClickOutside(extendedSearchDivRef as React.RefObject<HTMLElement>, closeExtendedSearch);

  const [enterPressed, setEnterPressed] = useState<boolean>(false);

  const clear = () => {
    if (inputElemRef?.current) {
      inputElemRef.current.value = '';
      inputElemRef.current.blur();
    }
    closeExtendedSearch();
  };

  useEffect(() => {
    if (clearSearchField) {
      handleClearAll();
    }
    if (defaultValues) {
      setChipInsideInput(defaultValues.searchType);
      if (defaultValues.searchModality === 'range') {
        setQuery(`${defaultValues.fromValue.toString()}-${defaultValues.toValue.toString()}`);
      } else {
        setQuery(defaultValues.searchValue.toString());
      }
    }
  }, [clearSearchField, defaultValues]);

  const performSearchCallback = useCallback(
    (_searchType: SearchType) => {
      const searchVariant = _searchType === 'empty' ? 'trainAndRollingStock' : _searchType;

      if (_searchType === 'empty') {
        setChipInsideInput('trainAndRollingStock');
        programmaticDateUpdate.current = true;
        setSelectedSearchDate(daySelectionVariations[0]);
      }
      const isSearchDateApplicable = ['trainAndRollingStock', 'train', 'omloop', 'bijzonderheidscode'].includes(
        searchVariant,
      );

      const searchParams = createSearchParams({
        searchDate: isSearchDateApplicable ? selectedSearchDate.date : null,
      });
      if (!searchParams) throw Error('Illegal state, unsupported state');
      if (_searchType !== 'json') updateSearchHistory(searchParams);
      if (onUserSearchIntent) onUserSearchIntent(searchParams, addSearchResults);
      clear();
    },
    [
      isValidSingle,
      isValidMaterieelTypeValue,
      isValidMultiple,
      isValidRange,
      isValidJson,
      isValidBijzonderheidscode,
      onUserSearchIntent,
      query,
      selectedSearchDate,
      daySelectionVariations,
      updateSearchHistory,
      createSearchParams,
      addSearchResults,
    ],
  );

  /**
   * Click handler for the SearchIcon.
   * The SearchIcon is not shown within the AdvancedSearchDialog.
   */
  const onSearchIconClick = () => {
    setFocused(true);
    setCloseSearchPanel(false);
    inputElemRef.current?.focus();
    if (
      (['trainAndRollingStock', 'train', 'rollingStock'].includes(searchType) && isValidSingleMultipleOrRangeValue) ||
      (searchType === 'empty' && isValidSingleMultipleOrRangeValue) ||
      (searchType === 'rollingStockType' && isValidMaterieelTypeValue && listItemIsSelected) ||
      (searchType === 'bijzonderheidscode' && isValidBijzonderheidscode)
    ) {
      performSearchCallback(searchType);
    } else {
      return null;
    }
  };

  // Handles passed to the parent container
  const focusHandler: FocusEventHandler<HTMLDivElement> = () => {
    if (!isWithinAdvancedSearch) {
      setFocused(true);
      setCloseSearchPanel(false);
    }
  };
  const handleOnClick = () => {
    if (isWithinAdvancedSearch) {
      setCloseSearchPanel(false);
      setFocused(true);
    }
  };
  /**
   * Automatically executes the search request, when the user clicks outside.
   */
  const onBlurHandler: FocusEventHandler<HTMLDivElement> = () => {
    if (
      isWithinAdvancedSearch &&
      ((searchType === 'trainAndRollingStock' && isValidSingleOrMultipleValue) ||
        ((searchType === 'train' || searchType === 'rollingStock') && isValidSingleMultipleOrRangeValue) ||
        isValidMaterieelTypeValue ||
        isValidOmloopValue)
    ) {
      performSearchCallback(searchType);
    }
  };

  /**
   * Add Search list integration. Enter already should work and perform the query,
   * but we still need to be able to select a different date.
   */
  const keyboardEventHandler = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (!e.altKey && !e.shiftKey && !e.ctrlKey) e.stopPropagation();

      if (e.key === 'Enter' || e.key === 'NumpadEnter') {
        /** Validation helper */
        const validate = (_type: SearchType, additional: boolean[] | boolean): boolean =>
          searchType === _type && (Array.isArray(additional) ? additional.every((check) => check) : additional);

        if (
          validate('rollingStock', isValidSingleMultipleOrRangeValue) ||
          validate('empty', [query.length !== 0, isValidSingleMultipleOrRangeValue]) ||
          validate('json', isValidJson) ||
          validate('rollingStockType', [isValidMaterieelTypeValue, listItemIsSelected]) ||
          validate('bijzonderheidscode', isValidBijzonderheidscode)
        ) {
          performSearchCallback(searchType);
        } else if (
          // Basically this validates all open lists and tells them to call handleSelectedListItem
          validate('trainAndRollingStock', isValidSingleMultipleOrRangeValue) ||
          validate('train', isValidSingleMultipleOrRangeValue) ||
          validate('omloop', isValidOmloopValue) ||
          validate('rollingStockType', !listItemIsSelected) ||
          validate('empty', [searchHistory.length > 0, query.length === 0])
        ) {
          setEnterPressed(true);
        }
        e.preventDefault();
      } else {
        setCloseSearchPanel(false);
      }
      if (e.key === 'Escape') {
        setCloseSearchPanel(true);
        setFocused(false);
        e.preventDefault();
      }
      if (['ArrowUp', 'ArrowDown'].includes(e.key)) {
        e.stopPropagation();
        e.preventDefault();
      }
      if (e.key === 'ArrowUp') arrowUp();
      if (e.key === 'ArrowDown') arrowDown();
    },
    [
      searchType,
      query,
      isValidJson,
      isValidSingleMultipleOrRangeValue,
      isValidOmloopValue,
      isValidBijzonderheidscode,
      listItemIsSelected,
      searchHistory,
      isValidMaterieelTypeValue,
      arrowDown,
      arrowUp,
      performSearchCallback,
    ],
  );

  const handleOnEnterPressed = useCallback(() => {
    setEnterPressed(false);
  }, []);

  const handleListItemIsSelected = useCallback((value) => {
    setListItemIsSelected(value);
  }, []);

  /** Handle for when the user clicks on a searchType */
  const setChipInsideInput = (_searchType: SearchType) => {
    setSelectedIndex(0);
    setSearchType(_searchType);
    setInputChipIsActive(true);
    if (inputElemRef?.current) {
      inputElemRef.current.focus();
    }
  };

  const handleRemoveChipOnly = useCallback(() => {
    if (isWithinAdvancedSearch) {
      handleResetFieldWithinAdvancedSearch?.();
    }
    setInputChipIsActive(false);
    setSearchType('empty');
  }, [handleResetFieldWithinAdvancedSearch, isWithinAdvancedSearch]);

  const handleClearAll = useCallback(() => {
    setInputChipIsActive(false);
    setSearchType('empty');
    setQuery('');
    if (inputElemRef?.current) {
      inputElemRef.current.value = '';
      inputElemRef.current.blur();
    }
    closeExtendedSearch();
    if (isWithinAdvancedSearch) {
      handleResetFieldWithinAdvancedSearch?.();
    }
  }, [inputElemRef, isWithinAdvancedSearch, handleResetFieldWithinAdvancedSearch]);

  const handleHistoryItemClick = useCallback(
    (historyItem: SearchParams) => {
      if (!historyItem || (isWithinAdvancedSearch && historyItem.searchType === 'rollingStockType')) return;
      setChipInsideInput(historyItem.searchType);
      setQuery(
        historyItem.searchModality === 'range'
          ? `${historyItem.fromValue}-${historyItem.toValue}`
          : historyItem.searchValue.toString(),
      );
      updateSearchHistory(historyItem);
      onUserSearchIntent?.(historyItem, addSearchResults);
      clear();
    },
    [addSearchResults],
  );

  // Only call the performSearchCallback for train when the user has selected a date
  useEffect(() => {
    if (programmaticDateUpdate.current) {
      programmaticDateUpdate.current = false;
      return;
    }

    if (
      searchType === 'trainAndRollingStock' ||
      searchType === 'train' ||
      searchType === 'omloop' ||
      searchType === 'bijzonderheidscode'
    ) {
      performSearchCallback(searchType);
    }
  }, [selectedSearchDate]);

  useEffect(() => {
    if (isWithinAdvancedSearch) {
      closeExtendedSearch();
    }
  }, [isWithinAdvancedSearch]);

  return (
    <div
      className={[styles.extendedSearchInput, className].join('')}
      onKeyDown={keyboardEventHandler}
      onFocus={focusHandler}
      onBlur={onBlurHandler}
      onClick={handleOnClick}
      ref={extendedSearchDivRef}
    >
      <SearchInput
        onSearchIconClick={onSearchIconClick}
        onChangeCallback={inputChangeHandler}
        handleOpenAdvancedSearchDialog={handleOpenAdvancedSearchDialog}
        placeholder='Zoek op nummer of reeks'
        minLength={3}
        isWithinAdvancedSearch={isWithinAdvancedSearch}
        hideSearchIcon={hideSearchIcon}
        value={query}
        searchTypeSelected={searchType}
        inputChipIsActive={inputChipIsActive}
        error={hasError && !focused}
        onClearAll={handleClearAll} // Main clear button
        onRemoveChip={handleRemoveChipOnly} // Chip only
        loading={loading}
        ref={inputRef}
      />
      {showPanel === 'searchPanel' && (
        <div className={styles.select}>
          <div style={{ padding: '24px', paddingBottom: '8px', textAlign: 'left' }}>
            <div
              data-testid='search-panel-title'
              onClick={handleShowJSONButtonClicks()}
              style={{
                paddingBottom: '12px',
                fontWeight: 500,
                color: 'var(--tractie-color-content-subtle)',
              }}
            >
              Waar wil je op zoeken?
            </div>
            <SearchTypeButtons
              setChipInsideInput={setChipInsideInput}
              isWithinAdvancedSearch={isWithinAdvancedSearch}
              showJSONChip={showJSONChip}
            />
            <div
              style={{
                paddingTop: '20px',
                paddingBottom: '12px',
                fontWeight: 500,
                color: 'var(--tractie-color-content-subtle)',
              }}
            >
              {searchHistory.length > 0 ? 'Recente zoekopdrachten' : ''}
            </div>
            <SearchPanelList
              isEmbedded
              searchPanelIsActive
              selectedListIndex={selectedIndex}
              setSelectedIndex={setSelectedIndex}
              returnSelectedListItem={handleHistoryItemClick}
              enterPressed={enterPressed}
              onEnterPressed={handleOnEnterPressed}
              data={searchHistory.filter(
                (historic) => !(isWithinAdvancedSearch && historic?.searchType === 'rollingStockType'),
              )}
            />
          </div>
          {!isWithinAdvancedSearch && (
            <ToggleAddSearchResult addSearchResults={addSearchResults} setAddSearchResults={onClickAddSearchResults} />
          )}
        </div>
      )}
      {showPanel === 'materieeltypen' && (
        <AutoCompletionList
          selectedListIndex={selectedIndex}
          setSelectedIndex={setSelectedIndex}
          ref={autoCompletionListRef}
          returnSelectedListItem={setSelectedItemInsideInput}
          data={materieeltypen || []}
          inputValue={query}
          enterPressed={enterPressed}
          onListItemIsSelected={handleListItemIsSelected}
          onEnterPressed={handleOnEnterPressed}
        >
          {!isWithinAdvancedSearch && (
            <ToggleAddSearchResult addSearchResults={addSearchResults} setAddSearchResults={onClickAddSearchResults} />
          )}
        </AutoCompletionList>
      )}
      {showPanel === 'searchDates' && (
        <SearchPanelList
          selectedListIndex={selectedIndex}
          setSelectedIndex={setSelectedIndex}
          ref={ref}
          returnSelectedListItem={setSelectedSearchDate as (item: any) => void}
          data={daySelectionVariations.map((v) => ({ ...v, searchType }))}
          searchPanelIsActive={query.length > 0}
          onEnterPressed={handleOnEnterPressed}
          enterPressed={enterPressed}
        >
          {!isWithinAdvancedSearch && (
            <ToggleAddSearchResult addSearchResults={addSearchResults} setAddSearchResults={onClickAddSearchResults} />
          )}
        </SearchPanelList>
      )}
      {hasError && <FieldErrorExtendedSearch smallGap={isWithinAdvancedSearch} errorText='Geen geldige invoer.' />}
    </div>
  );
}

export default ExtendedSearch;
